// src/components/History/SalesHistoryGrid.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Typography, Button, Stack } from '@mui/material';
import { fetchSales } from '../../actions/sales';
import SaleDetailsDialog from './SaleDetailsDialog';
import SaleSummaryDialog from '../Sales/SalesSummaryDialog';

const SalesHistoryGrid = ({ filter = {}, search = '' }) => {
  const dispatch = useDispatch();
  const items = useSelector((s) => s.sales.list || []);

  const [selectedSale, setSelectedSale] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  // Fetch sales when filter changes
  useEffect(() => {
    dispatch(fetchSales(filter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const range = filter?.range || 'all';

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const cutoff7 = new Date(startOfToday);
    cutoff7.setDate(cutoff7.getDate() - 7);

    const cutoff30 = new Date(startOfToday);
    cutoff30.setDate(cutoff30.getDate() - 30);

    return (items || [])
      .filter((s) => s && (s._id || s.id))
      .map((s) => {
        const created = s.createdAt ? new Date(s.createdAt) : null;
        const createdTs = created ? created.getTime() : 0;

        const dateStr = created ? created.toLocaleDateString() : '—';
        const timeStr = created
          ? created.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : '—';
        const itemsCount = Array.isArray(s.items) ? s.items.length : 0;
        const medicinesJoined = Array.isArray(s.items)
          ? s.items.map((i) => i.medicineName).filter(Boolean).join(', ')
          : '';

        return {
          id: s._id,
          refNo: s.refNo,
          paymentMethod: s.paymentMethod,
          date: dateStr,
          time: timeStr,
          itemsCount,
          subTotal: s.subTotal,
          total: s.total,
          medicinesJoined,
          createdAt: created,
          createdTimestamp: createdTs, // 🔹 for sorting/filtering
          sale: s,
        };
      })
      // 🔹 client-side date range filter
      .filter((row) => {
        if (range === 'all') return true;

        if (!row.createdAt) return false;
        const created = row.createdAt;

        if (range === 'today') {
          return created >= startOfToday;
        }
        if (range === '7d') {
          return created >= cutoff7;
        }
        if (range === '30d') {
          return created >= cutoff30;
        }

        return true;
      })
      // 🔹 text search
      .filter((row) => {
        if (!term) return true;
        return (
          (row.refNo || '').toLowerCase().includes(term) ||
          (row.paymentMethod || '').toLowerCase().includes(term) ||
          (row.medicinesJoined || '').toLowerCase().includes(term)
        );
      });
  }, [items, search, filter]);

  const columns = useMemo(
    () => [
      // 🔹 hidden column just for sorting
      {
        field: 'createdTimestamp',
        headerName: 'Created',
        width: 150,
        hide: true,
      },
      {
        field: 'refNo',
        headerName: 'Ref No',
        minWidth: 160,
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value || '—'}
          </Typography>
        ),
      },
      {
        field: 'date',
        headerName: 'Date',
        width: 120,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'time',
        headerName: 'Time',
        width: 100,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'paymentMethod',
        headerName: 'Payment',
        width: 120,
        headerAlign: 'left',
        align: 'left',
        renderCell: ({ value }) => (
          <span style={{ textTransform: 'capitalize' }}>{value || '—'}</span>
        ),
      },
      {
        field: 'itemsCount',
        headerName: 'Items',
        width: 90,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'subTotal',
        headerName: 'Subtotal',
        width: 130,
        headerAlign: 'left',
        align: 'left',
        renderCell: ({ value }) => {
          const num = Number(value);
          return <span>{Number.isFinite(num) ? `Rs ${num.toFixed(2)}` : '—'}</span>;
        },
      },
      {
        field: 'total',
        headerName: 'Total',
        width: 130,
        headerAlign: 'left',
        align: 'left',
        renderCell: ({ value }) => {
          const num = Number(value);
          return (
            <Typography sx={{ fontWeight: 700 }}>
              {Number.isFinite(num) ? `Rs ${num.toFixed(2)}` : '—'}
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 160,
        sortable: false,
        filterable: false,
        align: 'center',
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedSale(params.row.sale);
                setDetailsOpen(true);
              }}
              sx={{
                border: '1px solid #1976d2',
                textTransform: 'capitalize',
                '&.MuiButton-root:hover': { boxShadow: 'none' },
                '&.Mui-focusVisible': { boxShadow: 'none' },
              }}
            >
              Details
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedSale(params.row.sale);
                setInvoiceOpen(true);
              }}
              sx={{
                backgroundColor: 'transparent',
                color: '#333',
                border: '1px solid #333',
                boxShadow: 'none',
                textTransform: 'capitalize',
                '&.MuiButton-root:hover': {
                  boxShadow: 'none',
                  background: '#ededed',
                },
                '&.Mui-focusVisible': {
                  boxShadow: 'none',
                  background: 'silver',
                },
              }}
            >
              Invoice
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <>
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          showToolbar
          slotProps={{
            toolbar: {
              showQuickFilter: false,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
          }}
          sx={{
            '& .MuiDataGrid-toolbar': {
              justifyContent: 'start !important',
              gap: 1,
              pl: 1,
              background: '#fafafa',
            },
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#fafafa',
              borderBottom: '1px solid #e5e7eb',
              fontWeight: 600,
              fontSize: '0.9rem',
              color: '#374151',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f3f4f6',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.9rem',
              color: '#111827',
              py: '15px',
            },
            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9fafb' },
            '& .MuiDataGrid-cell:first-of-type': { fontWeight: 700 },
          }}
          pageSizeOptions={[5, 10, 20]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
            sorting: {
              // 🔹 earliest invoices first
              sortModel: [{ field: 'createdTimestamp', sort: 'desc' }],
            },
          }}
          paginationMode="client"
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
        />
      </Box>

      {selectedSale && (
        <>
          <SaleDetailsDialog
            open={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            sale={selectedSale}
          />
          <SaleSummaryDialog
            open={invoiceOpen}
            onClose={() => setInvoiceOpen(false)}
            sale={selectedSale}
          />
        </>
      )}
    </>
  );
};

export default SalesHistoryGrid;
